/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useEffect, useState } from 'react'
import * as HighCharts from 'highcharts'
import { Box, Card, CardContent, CircularProgress, Tab, Tabs, Typography, styled } from '@mui/material'
import DeviceTableRegReading from './dataSourceComponents/DeviceTableRegReading'
import DeviceTableReg from './dataSourceComponents/DeviceTableReg'
import HighchartsReact from 'highcharts-react-official'
import DarkUnica from 'highcharts/themes/dark-unica'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { useAuth } from 'react-oidc-context'
import { DesktopDateTimePicker } from '@mui/x-date-pickers'
import { GenericDropdown, type Option } from '../../../components/GenericDropdown'
import { type ChannelDataState } from '../redux/slices/channelDataSlice'
import { type ChannelDropdownData } from '../services/channelDropDownService'
import { type channelDropdownInterface } from '../redux/slices/channelDropdownSlice'
import LanguageStrings from '../../../i18n/locales'
import { getDatePickerInfo } from '../../../utils/datePickerInfo'
import { useDispatch } from 'react-redux'
import { channelDataReset } from '../redux/slices/channelDataSlice'

const DataSourceStrings = LanguageStrings().DatasourceStrings

function a11yProps (index: number): any {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

export interface DataSourceLabllUnit {
  label: string
  unit: string
}

interface ParentProps {
  fetchChannelData: (assetId: string, fromDate: string, toDate: string, selectedChannel: string[], selectedChannelUnit: string, tenantId: string, channelAttributes: ChannelDropdownData | undefined) => void
  chartData: ChannelDataState
  channelDropdownData: channelDropdownInterface
  fetchChannelDropdownData: (assetId: string, fromDate: string, toDate: string, selectedChannel: string, selectedChannelUnit: string, tenantId: string) => void
  fetchWaterKpiData: (assetId: string, fromDate: string, toDate: string, selectedChannelUnit: string, selectedKpi: string[], tenantId: string, channelAttributes: ChannelDropdownData | undefined) => void
  deviceType: string
}
const DataSource = (props: ParentProps): JSX.Element => {
  const [dropdownOptions, setDropdownOptions] = useState<Option[]>([])
  let paramAssetId = sessionStorage.getItem('paramAssetId')
  const auth = useAuth()
  // istanbul ignore next
  if (paramAssetId === null) {
    paramAssetId = ''
  }
  const defaultDate = {
    end: dayjs(new Date()),
    start: dayjs().subtract(29, 'day')
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [value2, setValue2] = React.useState(1)
  const [start, setStart] = useState<Dayjs | null | undefined>(defaultDate.start)
  const [end, setEnd] = useState<Dayjs | null | undefined>(defaultDate.end)
  const [selectedChannelName, setSelectedChannelName] = useState('')
  const [selectedChannel, setSelectedChannel] = useState<string[]>([])
  const [selectedChannelUnit, setSelectedChannelUnit] = useState('')
  const [channelMap, setChannelMap] = useState<Map<string, string[]>>(new Map())
  const dispatch = useDispatch()
  DarkUnica(HighCharts)
  const activeStyle = { fontSize: '12px', color: '#F0F0F0', borderBottom: '2px solid #1792E5' }
  const inActiveStyle = { fontSize: '12px', color: '#A0A0A0' }

  const isWaterAsset = (assetType: string): boolean => {
    return assetType.toLowerCase().includes('water')
  }
  const ninetyDayDate = React.useMemo(() => dayjs(end).subtract(89, 'day'), [end])

  const fetchChannel = (): any => {
    // istanbul ignore else
    if (paramAssetId !== undefined && start !== (undefined) && start !== null && end !== undefined && end !== null && start <= end && selectedChannel.length > 0 && selectedChannelName) {
      const startMoment = start
        .clone()
        .set('second', 0)
      const endMoment = end
        .clone()
        .set('second', 59)
      if (isWaterAsset(props.deviceType) && selectedChannelName.toLowerCase().includes('flow')) {
        props.fetchWaterKpiData(paramAssetId ?? '', startMoment.format('YYYY-MM-DDTHH:mm:ssZ'), endMoment.format('YYYY-MM-DDTHH:mm:ssZ'), selectedChannelUnit, selectedChannel, auth.user?.profile.customer as string, props.channelDropdownData.channelDropdownOptionData?.find(item => item.id === selectedChannel[0]))
      } else {
        props.fetchChannelData(paramAssetId ?? '', startMoment.format('YYYY-MM-DD HH:mm:ss'), endMoment.format('YYYY-MM-DD HH:mm:ss'), selectedChannel, selectedChannelUnit, auth.user?.profile.customer as string, props.channelDropdownData.channelDropdownOptionData?.find(item => item.id === selectedChannel[0]))
      }
    }
  }

  // istanbul ignore next
  const handleDateChange = (type: string, date: dayjs.Dayjs): void => {
    if (type === 'start') {
      if (date.isAfter(end) || date.isSame(end)) {
        setEnd(date)
      }
      setStart(date)
    } else {
      const newEnd = date
      let newStart = start?.startOf('day')

      // If gap > 90 days, shift start to end - 89, so that start will fall exactly at 1st day from end.
      const diff = newEnd.diff(newStart, 'day')
      if (diff > 89) {
        newStart = newEnd.subtract(89, 'day')
      } else if (newEnd.isBefore(newStart) || newEnd.isSame(newStart)) {
        newStart = newEnd
      }
      setStart(newStart)
      setEnd(newEnd)
    }
  }

  useEffect(() => {
    if (dropdownOptions.length === 0 && selectedChannelName === '') {
      dispatch(channelDataReset())
    }
  }, [dropdownOptions, selectedChannelName, dispatch])

  useEffect(() => {
    fetchChannel()
  }, [start, end, selectedChannel, selectedChannelName])

  function getGraph (): JSX.Element {
    if (selectedChannel.length === 0) {
      return (
        <div className='authError'>
          <AuthError errorMessage="NoDataPresent" />
        </div>
      )
    } else if (props.chartData.isLoading) {
      return <div style={{ textAlign: 'center' }}><CircularProgress /></div>
    } else if (props.chartData.httpStatus === 401) {
      return (
        <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (props.chartData.httpStatus === 403) {
      return (
        <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
      )
    } else if ((props.chartData.channelDataOptions === null && props.chartData.errorMessage !== null && props.chartData.httpStatus === 200)) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    } else if (((props.chartData.errorMessage?.includes('ChannelId is invalid') || props.chartData.errorMessage?.includes('channelId cannot be empty')) || props.chartData.errorMessage === 'No data found')) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (
      props.chartData.channelDataOptions !== undefined &&
      props.chartData.channelDataOptions !== null
    ) {
      return (
        <HighchartsReact
          highcharts={HighCharts}
          options={props.chartData.channelDataOptions?.consumptionChartOptions}
        />
      )
    } else {
      return (
        <div className='authError'><AuthError errorMessage="cannotFetch" retry={fetchChannel}/></div>
      )
    }
  }

  const WhiteDatePicker = styled(DesktopDateTimePicker)({
    '& input': {
      color: '#D0D0D0',
      fontSize: '14px',
      paddingRight: 0,
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  })
  const groupByChannelName = (channels: ChannelDropdownData[]): Map<string, string[]> => {
    const map = new Map<string, string[]>()

    for (const { name, id } of channels) {
      if (name == null || id == null) continue
      const key = String(name).trim().toLowerCase()

      if (!map.has(key)) map.set(key, [])
      const arr = map.get(key)
      if (arr) {
        arr.push(String(id))
      }
    }
    return map
  }

  const getChannelDropdown = (channelDropdownOptionData: Map<string, string[]>): void => {
    const tempDropdownOptions: Option[] = []
    const originalChannelData = props.channelDropdownData.channelDropdownOptionData
    for (const [, value] of channelDropdownOptionData) {
      const firstId = value[0]
      const originalName =
        originalChannelData?.find(item => String(item.id) === firstId)?.name ?? ''
      const opt: Option = { label: originalName, value: value.join(',') }
      tempDropdownOptions.push(opt)
    }
    setDropdownOptions(tempDropdownOptions)
  }

  useEffect(() => {
    const data = props.channelDropdownData.channelDropdownOptionData
    if (!data || selectedChannel.length === 0) {
      setSelectedChannelUnit('')
      setSelectedChannelName('')
      return
    }
    const firstId = selectedChannel[0]
    const selected = data.find(item => item.id === firstId)
    setSelectedChannelUnit(selected?.unit ?? '')
    setSelectedChannelName(selected?.name ?? '')
  }, [selectedChannel, props.channelDropdownData.channelDropdownOptionData])

  useEffect(() => {
    if (props.channelDropdownData.channelDropdownOptionData !== null && props.channelDropdownData.channelDropdownOptionData !== undefined) {
      setChannelMap(groupByChannelName(props.channelDropdownData.channelDropdownOptionData))
    }
  }, [props.channelDropdownData.channelDropdownOptionData])
  useEffect(() => {
    // Pass the channelDropdownOptionData array directly to the function
    // istanbul ignore next
    if (channelMap.size > 0) {
      getChannelDropdown(channelMap)
      const firstEntry = channelMap.entries().next().value as [string, string[]] | undefined
      setSelectedChannel(firstEntry ? firstEntry[1] : [])
    }
  }, [channelMap])

  const CustomLocalizationProviderWrapper = styled('div')({
    backgroundColor: '#505050',
    border: 'none',
    borderRadius: '4px'
  })

  // istanbul ignore next
  const handleOnChange = (e: any): void => {
    const channelIdArr = e.target.value.split(',').map((each: string) => each.trim()).filter(Boolean)
    setSelectedChannel(channelIdArr)
    if (props.channelDropdownData.channelDropdownOptionData !== null && props.channelDropdownData.channelDropdownOptionData !== undefined) {
      const selectedChannelData = props.channelDropdownData.channelDropdownOptionData.find((item) => item.id === channelIdArr[0])
      setSelectedChannelUnit(selectedChannelData?.unit ?? '')
      setSelectedChannelName(selectedChannelData?.name ?? '')
    }
  }
  return (

    <div className='devicedetails'>
      <Box sx={{ width: '100%', padding: '1em' }}>
        <Card sx={{ backgroundColor: '#313030', color: '#f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '16px', padding: '2em 2em' }}>{DataSourceStrings.Datasource.toUpperCase()}</div>
          <div style={{ display: 'flex' }}>
            <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
              <span style={{ paddingRight: '6px', fontSize: '14px' }}>
              {DataSourceStrings.StartDate}
              </span>
              <span className='datePicker-deviceDetails'>
                <CustomLocalizationProviderWrapper>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <WhiteDatePicker minDate={ninetyDayDate} disableFuture closeOnSelect={false} ampm={false} timeSteps={{ minutes: 1 }} slotProps={{ textField: { size: 'small', inputProps: { title: start?.format('MM/DD/YYYY HH:mm') } }, actionBar: { actions: ['cancel', 'accept'] }, field: { readOnly: true } }} defaultValue={defaultDate.start} value={start} onAccept={(val: any) => { handleDateChange('start', val) }} />
                  </LocalizationProvider>
                </CustomLocalizationProviderWrapper>
              </span>
            </span>
            <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
              <span style={{ paddingRight: '6px', fontSize: '14px' }}>
              {DataSourceStrings.EndDate}
              </span>
              <span className='datePicker-deviceDetails'>
                <CustomLocalizationProviderWrapper>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <WhiteDatePicker disableFuture closeOnSelect={false} ampm={false} timeSteps={{ minutes: 1 }} slotProps={{ textField: { size: 'small', inputProps: { title: end?.format('MM/DD/YYYY HH:mm') } }, actionBar: { actions: ['cancel', 'accept'] }, field: { readOnly: true } }} defaultValue={defaultDate.end} value={end} onAccept={(val: any) => { handleDateChange('end', val) }} />
                  </LocalizationProvider>
                </CustomLocalizationProviderWrapper>
              </span>
              <span style={{ marginRight: '1.5rem', marginBottom: '1.5rem' }}>
                      {getDatePickerInfo()}
               </span>
            </span>
            <span style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}>
              <span style={{ paddingRight: '6px', fontSize: '14px' }}>
                {DataSourceStrings.Datasource}
              </span>
              <span style={{ paddingRight: '1em', fontSize: '14px' }}>
              <GenericDropdown dataTestId='channel-dropdown' width={250} options={dropdownOptions} value={selectedChannel.join(',')} onChange={(e) => { handleOnChange(e) }} label={''} />
              </span>
            </span>
          </div>
        </div>
        <CardContent>
          {getGraph()}
          </CardContent>
        </Card>
        <Typography color='inherit' sx={{ fontSize: '20px', fontFamily: 'Honeywell Sans Web', paddingTop: '1em' }}>{((props.deviceType === 'gas-ei6' || props.deviceType === 'gas-ei7') ? DataSourceStrings.PARAMETERS : DataSourceStrings.REGISTERS)}</Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value2} aria-label='basic tabs example'>
            <Tab style={activeStyle} label={((props.deviceType === 'gas-ei6' || props.deviceType === 'gas-ei7') ? DataSourceStrings.READINGS : DataSourceStrings.REGISTER_READINGS)} {...a11yProps(1)} />
            <Tab style={{ ...inActiveStyle, display: 'none' }} disabled label={DataSourceStrings.REGISTERS} {...a11yProps(0)} aria-label='basic tabs 1' />
          </Tabs>
        </Box><div>
      {value2 === 1
        ? (
          <DeviceTableRegReading />
          )
        : (
          <DeviceTableReg />
          )}
    </div>

      </Box>
    </div>
  )
}
export default DataSource
